import React, {useContext, useState} from 'react';
import MuiDrawer, {drawerClasses} from '@mui/material/Drawer';
import {alpha, useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useLocation, Link} from 'react-router-dom';
import {Ranks} from '@embertracking/common';

import {UserContext} from '-/contexts/User';
import {stationDisplayName} from '@embertracking/common';
import {SettingsContext} from '-/contexts/Settings';

import HeaderBreadcrumbs from './HeaderBreadcrumbs';
import {HeaderSlotTarget, HeaderBannerSlotTarget, useHeaderStatusReserved} from '-/contexts/HeaderSlot';
import UserAvatar from '-/components/UserAvatar';

import Navbar from './navigation/Navbar';
import DesktopMenuContent from './navigation/DesktopMenuContent';
import UserMenu from './navigation/UserMenu';
import useMenuItems from './navigation/useMenuItems';
import useMenuPreferences from './navigation/useMenuPreferences';

import FirebaseImage from './FirebaseImage';
import Banners from './Banners';
import WhatsNewDialog from './WhatsNewDialog';
import GlobalSearch from './search/GlobalSearch';
import {Header} from '@embertracking/ui/components';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 64;
const AppContainer = props => {
    const {children} = props;
    const {currentUser} = useContext(UserContext);
    const {image} = useContext(SettingsContext);
    const {pathname} = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const statusReserved = useHeaderStatusReserved();

    const allItems = useMenuItems();
    const preferences = useMenuPreferences(allItems);
    const {isCollapsed, toggleCollapsed} = preferences;

    const handleToggleMenu = () => {
        if (isMobile) {
            setMobileMenuOpen(prev => !prev);
        } else {
            toggleCollapsed();
        }
    };

    // /manage (emailed auth-action links) is a standalone screen even when
    // signed in — most people open those links in the browser they're signed in
    // to, and the app shell (and its What's New popup) would sit over it.
    if (!currentUser || pathname === '/manage') {
        return children;
    }

    const {email, fullName, role, isStation} = currentUser || {};
    // A station account's currentUser is the station doc spread flat, with a
    // fullName the api composed. Format from the station fields directly rather
    // than trusting that string: the api and the SPA deploy separately, so this
    // is right for a department whose stations are named "Station 1" even
    // before the api-side fix ships (@embertracking/common).
    const displayName = (isStation ? stationDisplayName(currentUser) : fullName) || email;
    // Fullscreen pages get a bounded, full-height flex-column content area (so a child
    // grid can flex-fill and scroll internally instead of growing the document). The gear,
    // settings and member shells opt in — anchored so they don't catch member/profile gear
    // *sections* or any future /foo/settings route.
    //
    // The member shell (#1561) covers /profile and a single member at
    // /users/:id — including /users/add, which has no nav rail but does own the
    // same pinned save bar. The roster at /users is an ordinary page.
    const isMemberShell = /^\/profile(\/|$)/.test(pathname) || /^\/users\/[^/]+/.test(pathname);
    // Driver Clearance (#1580) is a sign-off matrix like /requirements/:section/matrix, it just
    // doesn't say "matrix" in its path. Both its tabs want the bounded layout — the matrix so it
    // fills the window, the log so it virtualises and scrolls internally instead of growing the
    // document (and so switching tabs doesn't swap the whole page between layouts).
    const isDrivingMatrix = /^\/requirements\/driverClearance(\/|$)/.test(pathname);
    // The calendar (#319) is `@mui/x-scheduler`, which sizes itself to its container
    // rather than to its content — a month grid, a time grid and a scrolling side
    // panel all need a real height to divide up. Unbounded it collapses to nothing.
    const isCalendar = /^\/calendar(\/|$)/.test(pathname);
    // Ask (#2204) is a chat: the page fills the window and the conversation
    // scrolls inside it, with the question box pinned at the bottom.
    const isAsk = /^\/ask(\/|$)/.test(pathname);
    const isFullscreen = /matrix|history/.test(pathname) || /^\/(gear|settings)(\/|$)/.test(pathname) || isMemberShell || isDrivingMatrix || isCalendar || isAsk;
    const drawerWidth = isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

    // Shared by both Header branches below. A station login is a shared
    // apparatus account on a truck-mounted tablet — it has no member identity to
    // hang recent searches off, and nothing on this list is its to look through.
    const headerSearch = isStation ? null : <GlobalSearch />;

    const headerActions = (
        <HeaderSlotTarget
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                maxWidth: '100%',
                '&:empty': {display: 'none'}
            }}
        />
    );

    return (
        <Box
            sx={theme => ({
                display: 'flex',
                // 100vh ignores mobile browser chrome, which parks the bottom of
                // a fullscreen page (the settings save bar, the gear grid's last
                // row) under the toolbar with nothing to scroll. dvh tracks the
                // visible viewport; the vh line is the fallback.
                height: '100vh',
                '@supports (height: 100dvh)': {
                    height: '100dvh'
                },
                '& .highlighted': {
                    backgroundColor: 'rgb(255, 242, 199, 0.8)',
                    fontWeight: 700
                },
                '& span.highlighted': {
                    position: 'relative'
                },
                '& span.highlighted:after': {
                    content: '""',
                    backgroundColor: 'rgb(255, 242, 199, 0.8)',
                    position: 'absolute',
                    top: -1,
                    bottom: -1,
                    right: -2,
                    left: -2,
                    zIndex: -1,
                    borderRadius: 1
                },
                backgroundColor: theme.vars
                    ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                    : alpha(theme.palette.background.default, 1)
            })}
            className="et-app-shell"
        >
            <MuiDrawer
                data-noprint
                variant="permanent"
                sx={{
                    display: {xs: 'none', md: 'block'},
                    width: drawerWidth,
                    flexShrink: 0,
                    transition: 'none',
                    [`& .${drawerClasses.paper}`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        backgroundColor: 'background.paper',
                        transition: 'none',
                        overflow: 'visible'
                    }
                }}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                ModalProps={{
                    keepMounted: true
                }}
            >
                <Box sx={{position: 'relative', overflow: 'visible'}}>
                    <Box
                        component={Link}
                        to="/"
                        sx={{
                            mt: 2,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            px: isCollapsed ? 0.5 : 0,
                            cursor: 'pointer'
                        }}
                    >
                        {image ? (
                            <FirebaseImage image={image} publicAsset sx={{width: isCollapsed ? 40 : '60%', transition: 'none'}} />
                        ) : (
                            <img src="/logo.png" alt="Ember Tracking" style={{width: isCollapsed ? 40 : '60%', transition: 'none'}} />
                        )}
                    </Box>
                </Box>

                <Box sx={{overflowX: 'hidden', flex: 1, minHeight: 0}}>
                    <DesktopMenuContent isCollapsed={isCollapsed} allItems={allItems} preferences={preferences} />
                </Box>

                <Stack
                    direction="row"
                    sx={{
                        width: '100%',
                        p: isCollapsed ? 1 : 2,
                        pr: isCollapsed ? 1 : 1,
                        alignItems: 'center',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {/* The identity itself is the link to your own profile — the
                        avatar and name are what people aim at, and collapsed there
                        is nothing else in this footer to click at all. A station
                        login has no member profile to open, so it stays inert.
                        The ⋮ menu is deliberately OUTSIDE the link: nesting a
                        button inside an anchor is invalid, and its clicks would
                        navigate on the way past. */}
                    <ButtonBase
                        {...(!isStation && {component: Link, to: '/profile'})}
                        disabled={isStation}
                        aria-label={isStation ? undefined : 'Open your profile'}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            // Only the expanded row shares space with the menu
                            // button; collapsed it's the avatar and nothing else.
                            ...(isCollapsed ? {} : {flex: 1}),
                            // Negative margin keeps the hit area padded without
                            // moving the avatar off the alignment it had before.
                            p: 0.5,
                            m: -0.5,
                            borderRadius: 1,
                            textAlign: 'left'
                        }}
                    >
                        <UserAvatar user={currentUser} sx={{width: 36, height: 36}} />
                        {!isCollapsed && (
                            <Stack sx={{flex: 1, ml: 1, minWidth: 0}}>
                                <Typography variant="body2" noWrap sx={{fontWeight: 500, lineHeight: '16px'}}>
                                    {displayName}
                                </Typography>
                                {!isStation && (
                                    <Typography variant="caption" noWrap sx={{color: 'text.secondary'}}>
                                        {Ranks[role]}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </ButtonBase>
                    {!isCollapsed && <UserMenu />}
                </Stack>
            </MuiDrawer>

            <Box data-noprint>
                <Navbar
                    allItems={allItems}
                    preferences={preferences}
                    mobileMenuOpen={mobileMenuOpen}
                    setMobileMenuOpen={setMobileMenuOpen}
                />
            </Box>

            <WhatsNewDialog />
            {/* The floating support tab (@embertracking/ui/components SupportTab)
                used to mount here. Parked while the feature soft-launches behind
                the admin-only /help page (#1670) — that page owns the entry point
                for now. Re-mount it here when Help & Tips (#777) ships and support
                should be reachable from every page again. */}

            {isFullscreen ? (
                <Box sx={{minWidth: 0, display: 'flex', flexDirection: 'column', width: '100%'}}>
                    <Banners />
                    <HeaderBannerSlotTarget data-noprint style={{display: 'block'}} />
                    <Header
                        data-noprint
                        onToggleMenu={handleToggleMenu}
                        toggleTitle={isMobile ? 'Open menu' : (isCollapsed ? 'Expand menu' : 'Collapse menu')}
                        statusReserved={statusReserved}
                        hideWhenEmpty
                        sx={{flexShrink: 0, px: {xs: 1.5, md: 3}}}
                        search={headerSearch}
                        actions={headerActions}
                    >
                        <HeaderBreadcrumbs />
                    </Header>

                    <Box sx={{p: 2, px: {xs: 1, sm: 2}, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                        {children}
                    </Box>
                </Box>
            ) : (
                <Box sx={{minWidth: 0, display: 'flex', flexDirection: 'column', width: '100%'}}>
                    <Banners />
                    <HeaderBannerSlotTarget data-noprint style={{display: 'block'}} />
                    <Header
                        data-noprint
                        onToggleMenu={handleToggleMenu}
                        toggleTitle={isMobile ? 'Open menu' : (isCollapsed ? 'Expand menu' : 'Collapse menu')}
                        statusReserved={statusReserved}
                        hideWhenEmpty
                        search={headerSearch}
                        actions={headerActions}
                    >
                        <HeaderBreadcrumbs />
                    </Header>

                    <Box sx={{p: 2, px: {xs: 1, sm: 2}, pb: 2}}>
                        {children}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AppContainer;
